import React, { useEffect, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import axios from 'axios';
import { BASE_URL } from '../context/api';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

Chart.register(...registerables);

const Dashboard = () => {
  const [financialData, setFinancialData] = useState([]);
  const [totalThisMonth, setTotalThisMonth] = useState(0);
  const [studentData, setStudentData] = useState({ totalStudents: 0, genderStats: [] });
  const [announcement, setAnnouncement] = useState(null);
  const [employeeData, setEmployeeData] = useState({ totalEmployees: 0, genderStats: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const financialsResponse = await axios.get(`${BASE_URL}/dashboard/financials`);
      const studentsResponse = await axios.get(`${BASE_URL}/dashboard/students`);
      const announcementResponse = await axios.get(`${BASE_URL}/dashboard/announcement`);
      const employeesResponse = await axios.get(`${BASE_URL}/dashboard/employees`);

      setFinancialData(financialsResponse.data.monthlyTotals || []);
      setTotalThisMonth(financialsResponse.data.currentMonthTotal || 0);
      setStudentData(studentsResponse.data || { totalStudents: 0, genderStats: [] });
      setAnnouncement(announcementResponse.data || null);
      setEmployeeData(employeesResponse.data || { totalEmployees: 0, genderStats: [] });
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="relative">
          <div className="w-12 h-12 rounded-full absolute border-4 border-solid border-pink-600 border-t-transparent animate-spin"></div>
          <div className="w-12 h-12 rounded-full absolute border-4 border-solid border-pink-400 border-t-transparent animate-spin" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-12 h-12 rounded-full absolute border-4 border-solid border-pink-200 border-t-transparent animate-spin" style={{ animationDelay: '0.4s' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
        <div className="h-px flex-grow bg-gray-200 ml-4"></div>
      </div>
      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Students</h3>
          <p className="text-3xl font-bold text-pink-600">{studentData.totalStudents}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Employees</h3>
          <p className="text-3xl font-bold text-pink-600">{employeeData.totalEmployees}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Monthly Revenue</h3>
          <p className="text-3xl font-bold text-pink-600">${totalThisMonth}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Latest Announcement</h3>
          {announcement ? (
            <Link
              to="/announcements"
              className="text-pink-600 hover:text-pink-800 text-sm"
            >
              View Announcements →
            </Link>
          ) : (
            <p className="text-gray-500 text-sm">No announcements</p>
          )}
        </div>
      </div>

      {/* Graphs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Financial Overview */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-xl font-semibold mb-4 text-gray-700">Financial Overview</h3>
          <div className="h-64">
            <Line
              data={{
                labels: financialData.map((item) => item.month),
                datasets: [
                  {
                    label: 'Total Amount',
                    data: financialData.map((item) => item.total),
                    borderColor: '#BB005A',
                    backgroundColor: 'rgba(187, 0, 90, 0.1)',
                    fill: true,
                    tension: 0.4,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    labels: {
                      color: '#333',
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      color: '#333',
                    },
                  },
                  x: {
                    ticks: {
                      color: '#333',
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Student Gender Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-xl font-semibold mb-4 text-gray-700">Student Gender Distribution</h3>
          <div className="h-64">
            <Pie
              data={{
                labels: studentData.genderStats.map((item) => item.Gender),
                datasets: [
                  {
                    data: studentData.genderStats.map((item) => item.count),
                    backgroundColor: ['#BB005A', '#4A90E2'],
                    borderColor: ['#BB005A', '#4A90E2'],
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      color: '#333',
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Employee Gender Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-xl font-semibold mb-4 text-gray-700">Employee Gender Distribution</h3>
          <div className="h-64">
            <Bar
              data={{
                labels: employeeData.genderStats.map((item) => item.Gender),
                datasets: [
                  {
                    label: 'Employees',
                    data: employeeData.genderStats.map((item) => item.count),
                    backgroundColor: ['#BB005A', '#4A90E2'],
                    borderColor: ['#BB005A', '#4A90E2'],
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      color: '#333',
                    },
                  },
                  x: {
                    ticks: {
                      color: '#333',
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
